import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, App, Button, Form, InputNumber, Progress, Radio, Select, Switch, } from 'antd';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useSession } from 'components/Authenticated';
import FormModal from 'components/FormModal';
import { fetchExportTemplates } from 'api/team-api';
import { SubtitlesFormatSchema } from 'api/settings/user-settings';
import { txt } from 'libs/i18n';
import { downloadBlobAsFile } from '../../../EditorPage/ProjectMenuPanel/exports';
import { SUBTITLE_LENGTH } from '../../../EditorPage/ProjectMenuPanel/CaptionReviewModal';
import { canUseSubtitleFormat } from '../../../EditorPage/ProjectMenuPanel/SubtitlesModal/format-claims';
import { BULK_EXPORT_FORMATS, bulkExportProjects, } from './bulk-export';
import styles from './style.module.less';
const { Option } = Select;
const failureText = (reason) => {
    if (reason === 'not-completed') {
        return txt('preCompleted');
    }
    if (reason === 'transcription-failed') {
        return txt('bulkExportTranscriptionFailed');
    }
    if (reason === 'transcription-canceled') {
        return txt('bulkExportTranscriptionCanceled');
    }
    if (reason === 'no-transcription' || reason === 'not-found') {
        return txt('noTranscription');
    }
    if (reason === 'invalid-subtitle-format') {
        return txt('invalidExportFormat');
    }
    if (reason === 'subtitles-failed') {
        return txt('subtitleExportMessageError');
    }
    return txt('exportUnexpectedError');
};
const BulkExportDialog = ({ selectedProjects, visible, onClose, onComplete, }) => {
    const [form] = Form.useForm();
    const { message } = App.useApp();
    const { session, updateUserSettings } = useSession();
    const [exportTemplates, setExportTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [phase, setPhase] = useState({ type: 'form' });
    const selectedFormat = Form.useWatch('format', form);
    const savedTemplate = session.login.user.settings.documentExportSettings.template;
    const savedSubtitlesExport = session.login.user.settings.subtitlesExport;
    const hasClaim = (claim) => session.login.hasClaim(claim);
    const allSubtitlesAllowed = hasClaim('subtitles:all');
    const allowedSubtitleFormats = SubtitlesFormatSchema.values
        .filter((format) => canUseSubtitleFormat(hasClaim, format, allSubtitlesAllowed));
    const initialSubtitlesFormat = allowedSubtitleFormats
        .includes(savedSubtitlesExport.subtitlesFormat)
        ? savedSubtitlesExport.subtitlesFormat
        : allowedSubtitleFormats[0];
    const availableFormats = allowedSubtitleFormats.length === 0
        ? BULK_EXPORT_FORMATS.filter((format) => format !== 'subtitles')
        : BULK_EXPORT_FORMATS;
    useEffect(() => {
        if (!visible) {
            setPhase({ type: 'form' });
            return;
        }
        const loadTemplates = async () => {
            setTemplatesLoading(true);
            try {
                setExportTemplates(await fetchExportTemplates(session.connection));
            }
            finally {
                setTemplatesLoading(false);
            }
        };
        void loadTemplates();
    }, [visible]);
    const buildExportOptions = (values, usesTemplate) => {
        if (values.format === 'subtitles') {
            return {
                format: 'subtitles',
                subtitles: {
                    subtitlesFormat: values.subtitlesFormat,
                    maxLineLength: values.maxLineLength,
                    upperCaseAllText: values.upperCaseAllText,
                    speakerSignPlacement: savedSubtitlesExport.speakerSignPlacement,
                    pauseBetweenCaptions: savedSubtitlesExport.pauseBetweenCaptions,
                    autofillPauseBetweenCaptions: savedSubtitlesExport.autofillPauseBetweenCaptionsMs / 1000,
                },
            };
        }
        if (values.format === 'trsx') {
            return { format: 'trsx' };
        }
        return {
            format: values.format,
            withTimeStamps: session.login.user.settings.enableTimestampLabels,
            exportTemplateId: usesTemplate ? values.template : undefined,
        };
    };
    const zipName = (format) => (`beey-${format}-${dayjs().format('YYYY-MM-DD')}.zip`);
    const handleSubmit = async (values) => {
        setPhase({ type: 'exporting', finishedCount: 0 });
        const usesTemplate = (values.format === 'docx' || values.format === 'pdf')
            && exportTemplates.length > 0;
        let result;
        try {
            result = await bulkExportProjects(session.connection, selectedProjects, buildExportOptions(values, usesTemplate), (finishedCount) => setPhase({ type: 'exporting', finishedCount }));
        }
        catch (_a) {
            void message.error(txt('exportUnexpectedError'));
            setPhase({ type: 'form' });
            return;
        }
        if (result.zipBlob !== null) {
            downloadBlobAsFile(zipName(values.format), result.zipBlob);
        }
        if (usesTemplate) {
            updateUserSettings({ documentExportSettings: { template: values.template } });
        }
        if (values.format === 'subtitles') {
            updateUserSettings({
                subtitlesExport: Object.assign(Object.assign({}, savedSubtitlesExport), { subtitlesFormat: values.subtitlesFormat, maxLineLength: values.maxLineLength, upperCaseAllText: values.upperCaseAllText }),
            });
        }
        if (result.failed.length === 0) {
            onComplete();
            return;
        }
        setPhase({ type: 'done', result });
    };
    const renderFooter = () => {
        if (phase.type === 'done') {
            return _jsx(Button, { type: "primary", onClick: onComplete, children: txt('close') });
        }
        return (_jsxs(_Fragment, { children: [_jsx(Button, { type: "default", disabled: phase.type === 'exporting', onClick: onClose, children: txt('cancel') }), _jsx(Button, { type: "primary", htmlType: "submit", disabled: templatesLoading, loading: phase.type === 'exporting' || templatesLoading, onClick: () => form.submit(), children: txt('download') })] }));
    };
    const formatLabel = (format) => (format === 'subtitles' ? txt('bulkExportSubtitlesFormat') : format);
    const renderSubtitlesFields = () => (_jsxs(_Fragment, { children: [_jsx(Form.Item, { name: "subtitlesFormat", label: txt('chooseSubtFormat'), children: _jsx(Select, { children: allowedSubtitleFormats.map((format) => (_jsx(Option, { value: format, children: format === 'srt_FB' ? txt('exportSrtFB') : format }, format))) }) }), _jsx(Form.Item, { name: "maxLineLength", label: `${txt('chooseLineLength')} (${txt('charactersPerLine')})`, required: true, rules: [
                    {
                        required: true,
                        type: 'number',
                        min: SUBTITLE_LENGTH.min,
                        max: SUBTITLE_LENGTH.max,
                        message: `${txt('range')} ${SUBTITLE_LENGTH.min} - ${SUBTITLE_LENGTH.max}`,
                    },
                ], children: _jsx(InputNumber, { type: "number" }) }), _jsx(Form.Item, { name: "upperCaseAllText", label: txt('setAllCaps'), valuePropName: "checked", children: _jsx(Switch, { size: "small" }) }), _jsx(Alert, { className: styles.subtitlesInfo, type: "info", showIcon: true, message: txt('bulkSubtitlesUseReviewedSettings') })] }));
    const renderBody = () => {
        if (phase.type === 'exporting') {
            return (_jsxs("div", { className: styles.progress, children: [_jsx("span", { children: `${txt('exportingProjects')}…` }), _jsx(Progress, { percent: Math.round((phase.finishedCount / selectedProjects.length) * 100), status: "active" })] }));
        }
        if (phase.type === 'done') {
            return (_jsxs("div", { children: [_jsx("p", { children: `${txt('exportedProjects')}: ${phase.result.exportedCount} / ${selectedProjects.length}` }), _jsx("p", { children: txt('notExportedProjects') }), _jsx("ul", { className: styles.failedList, children: phase.result.failed.map(({ project, reason }) => (_jsxs("li", { children: [_jsx("strong", { children: project.description.name }), ` – ${failureText(reason)}`] }, project.id))) })] }));
        }
        return (_jsxs(Form, { form: form, layout: "vertical", initialValues: {
                format: 'docx',
                template: savedTemplate,
                subtitlesFormat: initialSubtitlesFormat,
                maxLineLength: savedSubtitlesExport.maxLineLength,
                upperCaseAllText: savedSubtitlesExport.upperCaseAllText,
            }, onFinish: handleSubmit, children: [_jsx(Form.Item, { name: "format", label: txt('format'), children: _jsx(Radio.Group, { options: availableFormats.map((format) => ({
                            value: format,
                            label: formatLabel(format),
                        })) }) }), exportTemplates.length > 0 && (selectedFormat === 'docx' || selectedFormat === 'pdf') && (_jsx(Form.Item, { name: "template", label: txt('exportTemplate'), children: _jsxs(Select, { children: [_jsx(Option, { value: "default", children: txt('speakerColorDefault') }), exportTemplates.map((template) => (_jsx(Option, { value: template.name, children: template.name }, template.name)))] }) })), selectedFormat === 'subtitles' && renderSubtitlesFields()] }));
    };
    return (_jsx(FormModal, { open: visible, onCancel: phase.type === 'done' ? onComplete : onClose, closable: phase.type !== 'exporting', keyboard: phase.type !== 'exporting', maskClosable: false, title: txt('bulkExport'), footer: renderFooter(), children: renderBody() }));
};
export default BulkExportDialog;
